/**
 * Special Service Request Component
 * Allows customers to submit custom service requests with AI-powered pricing suggestions
 * Now includes AI Vehicle Diagnostic integration
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AIVehicleDiagnostic from './AIVehicleDiagnostic';
import TippingDonation from './TippingDonation';
import PricingBreakdown from './PricingBreakdown';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Sparkles, Loader2, DollarSign, Clock, AlertCircle, 
  CheckCircle2, ArrowRight, Zap, Info, ChevronDown, ChevronUp,
  Car, Wrench
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Service categories for special requests
const serviceCategories = [
  { id: 'roadside_emergency', label: 'Roadside Emergency', icon: '🚨' },
  { id: 'mechanical_repair', label: 'Mechanical Repair', icon: '🔧' },
  { id: 'electrical_issue', label: 'Electrical Issue', icon: '⚡' },
  { id: 'tire_service', label: 'Tire Service', icon: '🛞' },
  { id: 'fluid_service', label: 'Fluid Service', icon: '🛢️' },
  { id: 'diagnostic', label: 'Diagnostic', icon: '🔍' },
  { id: 'custom', label: 'Other / Custom', icon: '📝' }
];

// Urgency levels
const urgencyLevels = [
  { id: 'emergency', label: 'Emergency', desc: 'Stranded/Safety hazard', color: 'red', multiplier: 1.5 },
  { id: 'urgent', label: 'Urgent', desc: 'Need help within 2 hours', color: 'orange', multiplier: 1.25 },
  { id: 'standard', label: 'Standard', desc: 'Within 24 hours', color: 'blue', multiplier: 1.0 },
  { id: 'flexible', label: 'Flexible', desc: 'Schedule at convenience', color: 'green', multiplier: 0.9 }
];

export default function SpecialServiceRequest({ onClose, onSubmitSuccess }) {
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1); // 1: Describe, 2: AI Pricing, 3: Confirm
  const [loading, setLoading] = useState(false);
  const [aiPricing, setAiPricing] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showDiagnostic, setShowDiagnostic] = useState(false);
  const [diagnosticData, setDiagnosticData] = useState(null);
  
  const [formData, setFormData] = useState({
    category: '',
    description: '',
    urgency: 'standard',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: new Date().getFullYear(),
    location_description: '',
    preferred_budget: '',
    additional_notes: ''
  });

  // Handle diagnostic completion
  const handleDiagnosticComplete = (data) => {
    setDiagnosticData(data);
    // Auto-fill form with diagnostic info
    if (data?.instructions) {
      const vehicle = data.instructions.vehicle;
      const problem = data.instructions.problem_summary;
      setFormData(prev => ({
        ...prev,
        vehicle_make: vehicle?.make || prev.vehicle_make,
        vehicle_model: vehicle?.model || prev.vehicle_model,
        vehicle_year: vehicle?.year || prev.vehicle_year,
        description: problem ? `${problem}\n\nAI Diagnostic: ${data.instructions.diagnostic_steps?.slice(0, 2).join('. ')}` : prev.description,
        category: data.instructions.system || prev.category
      }));
      setShowDiagnostic(false);
      toast.success('Diagnostic info added to your request!');
    }
  };

  // Validate step 1
  const canProceedStep1 = () => {
    return formData.category && formData.description.length >= 20;
  };

  // Get AI pricing suggestion
  const getAIPricing = async () => {
    if (!canProceedStep1()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/ai/price-estimate`,
        {
          category: formData.category,
          description: formData.description,
          urgency: formData.urgency,
          vehicle_info: {
            make: formData.vehicle_make,
            model: formData.vehicle_model,
            year: formData.vehicle_year
          },
          preferred_budget: formData.preferred_budget ? parseFloat(formData.preferred_budget) : null
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      setAiPricing(response.data);
      setStep(2);
    } catch (error) {
      console.error('AI pricing error:', error);
      // Fallback to basic estimate if AI fails
      const baseEstimate = getBasicEstimate(formData.category);
      const urgencyMultiplier = urgencyLevels.find(u => u.id === formData.urgency)?.multiplier || 1;
      
      setAiPricing({
        suggested_price: Math.round(baseEstimate * urgencyMultiplier),
        min_price: Math.round(baseEstimate * 0.7 * urgencyMultiplier),
        max_price: Math.round(baseEstimate * 1.5 * urgencyMultiplier),
        confidence: 0.7,
        reasoning: "Based on typical service costs for this category.",
        factors: [
          { factor: "Service Category", impact: "Base pricing" },
          { factor: "Urgency Level", impact: `${urgencyMultiplier}x multiplier` }
        ],
        ai_powered: false
      });
      setStep(2);
    } finally {
      setLoading(false);
    }
  };

  // Basic estimate fallback
  const getBasicEstimate = (category) => {
    const estimates = {
      'roadside_emergency': 75,
      'mechanical_repair': 150,
      'electrical_issue': 100,
      'tire_service': 60,
      'fluid_service': 55,
      'diagnostic': 80,
      'custom': 100
    };
    return estimates[category] || 100;
  };

  // Submit special request
  const submitRequest = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/services/special-request`,
        {
          ...formData,
          ai_suggested_price: aiPricing?.suggested_price,
          customer_accepted_price: aiPricing?.suggested_price,
          pricing_data: aiPricing
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      toast.success('Special request submitted! Waiting for provider approval.');
      
      if (onSubmitSuccess) {
        onSubmitSuccess(response.data);
      } else {
        navigate(`/customer/tracking/${response.data.request_id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const selectedCategory = serviceCategories.find(c => c.id === formData.category);
  const selectedUrgency = urgencyLevels.find(u => u.id === formData.urgency);

  return (
    <Card className="w-full max-w-2xl mx-auto" data-testid="special-service-request">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <CardTitle className="text-xl">Special Service Request</CardTitle>
            <CardDescription>Describe your unique situation for a custom quote</CardDescription>
          </div>
        </div>
        
        {/* Step Indicator */}
        <div className="flex items-center gap-2 mt-4">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                step === s 
                  ? 'bg-blue-600 text-white' 
                  : step > s 
                    ? 'bg-green-500 text-white' 
                    : 'bg-gray-200 text-gray-500'
              }`}>
                {step > s ? <CheckCircle2 className="w-4 h-4" /> : s}
              </div>
              {s < 3 && (
                <div className={`w-12 h-1 mx-1 rounded ${
                  step > s ? 'bg-green-500' : 'bg-gray-200'
                }`} />
              )}
            </div>
          ))}
          <span className="ml-2 text-sm text-gray-500">
            {step === 1 ? 'Describe' : step === 2 ? 'Review Price' : 'Confirm'}
          </span>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Step 1: Describe the issue */}
        {step === 1 && (
          <>
            {/* Category Selection */}
            <div>
              <Label className="text-sm font-medium mb-2 block">What type of service do you need?</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {serviceCategories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, category: cat.id }))}
                    className={`p-3 border-2 rounded-lg text-left transition-all ${
                      formData.category === cat.id
                        ? 'border-blue-600 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                    data-testid={`category-${cat.id}`}
                  >
                    <span className="text-xl mb-1 block">{cat.icon}</span>
                    <span className="text-sm font-medium">{cat.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Problem Description */}
            <div>
              <Label htmlFor="description" className="text-sm font-medium">
                Describe your problem in detail *
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Example: My car won't start. When I turn the key, I hear a clicking sound but the engine doesn't turn over. The dashboard lights come on but dim when I try to start..."
                className="mt-1.5 min-h-[120px]"
                data-testid="problem-description"
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.description.length}/20 minimum characters
                {formData.description.length >= 20 && (
                  <CheckCircle2 className="w-3 h-3 text-green-500 inline ml-1" />
                )}
              </p>
            </div>

            {/* Urgency Level */}
            <div>
              <Label className="text-sm font-medium mb-2 block">How urgent is this?</Label>
              <div className="grid grid-cols-2 gap-2">
                {urgencyLevels.map((level) => (
                  <button
                    key={level.id}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, urgency: level.id }))}
                    className={`p-3 border-2 rounded-lg text-left transition-all ${
                      formData.urgency === level.id
                        ? `border-${level.color}-500 bg-${level.color}-50`
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`urgency-${level.id}`}
                  >
                    <span className={`text-sm font-semibold text-${level.color}-600`}>{level.label}</span>
                    <p className="text-xs text-gray-500">{level.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Vehicle Info (Optional) */}
            <div>
              <button
                type="button"
                onClick={() => setShowDetails(!showDetails)}
                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
              >
                {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showDetails ? 'Hide' : 'Add'} vehicle details (optional)
              </button>
              
              {showDetails && (
                <div className="mt-3 grid grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="vehicle_make" className="text-xs">Make</Label>
                    <Input
                      id="vehicle_make"
                      value={formData.vehicle_make}
                      onChange={(e) => setFormData(prev => ({ ...prev, vehicle_make: e.target.value }))}
                      placeholder="Toyota"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="vehicle_model" className="text-xs">Model</Label>
                    <Input
                      id="vehicle_model"
                      value={formData.vehicle_model}
                      onChange={(e) => setFormData(prev => ({ ...prev, vehicle_model: e.target.value }))}
                      placeholder="Camry"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="vehicle_year" className="text-xs">Year</Label>
                    <Input
                      id="vehicle_year"
                      type="number"
                      value={formData.vehicle_year}
                      onChange={(e) => setFormData(prev => ({ ...prev, vehicle_year: parseInt(e.target.value) }))}
                      className="mt-1"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* AI Vehicle Diagnostic Tool */}
            {showDiagnostic ? (
              <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium text-blue-800 flex items-center gap-2">
                    <Car className="w-5 h-5" />
                    AI Vehicle Diagnostic
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowDiagnostic(false)}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Close
                  </button>
                </div>
                <AIVehicleDiagnostic 
                  onDiagnosticComplete={handleDiagnosticComplete}
                  initialVehicle={{
                    make: formData.vehicle_make,
                    model: formData.vehicle_model,
                    year: formData.vehicle_year
                  }}
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowDiagnostic(true)}
                className="w-full p-3 border-2 border-dashed border-blue-300 rounded-lg text-blue-600 hover:bg-blue-50 flex items-center justify-center gap-2 transition-colors"
                data-testid="open-diagnostic-btn"
              >
                <Car className="w-5 h-5" />
                <span className="font-medium">Use AI Diagnostic Tool</span>
                <span className="text-xs bg-blue-100 px-2 py-0.5 rounded">VIN Lookup + Work Instructions</span>
              </button>
            )}

            {/* Diagnostic data preview */}
            {diagnosticData && !showDiagnostic && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 text-green-800 text-sm">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="font-medium">AI Diagnostic attached</span>
                </div>
                <p className="text-xs text-green-700 mt-1">
                  {diagnosticData.instructions?.diagnostic_steps?.length || 0} diagnostic steps, 
                  {diagnosticData.instructions?.likely_components?.length || 0} likely parts identified
                </p>
              </div>
            )}
            <div>
              <Label htmlFor="preferred_budget" className="text-sm font-medium">
                Your budget (optional)
              </Label>
              <div className="relative mt-1.5">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="preferred_budget"
                  type="number"
                  value={formData.preferred_budget}
                  onChange={(e) => setFormData(prev => ({ ...prev, preferred_budget: e.target.value }))}
                  placeholder="Enter your max budget"
                  className="pl-9"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                This helps us match you with the right provider
              </p>
            </div>

            {/* Action Button */}
            <Button
              onClick={getAIPricing}
              disabled={!canProceedStep1() || loading}
              className="w-full h-12 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              data-testid="get-ai-pricing-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Get AI Price Estimate
                </>
              )}
            </Button>
          </>
        )}

        {/* Step 2: AI Pricing Review */}
        {step === 2 && aiPricing && (
          <>
            {/* AI Pricing Card */}
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-100">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-purple-600" />
                  <span className="font-semibold text-gray-900">AI Price Estimate</span>
                </div>
                <Badge variant={aiPricing.ai_powered !== false ? "default" : "secondary"}>
                  {aiPricing.ai_powered !== false ? 'AI-Powered' : 'Basic Estimate'}
                </Badge>
              </div>

              {/* Main Price */}
              <div className="text-center py-4">
                <p className="text-sm text-gray-500 mb-1">Suggested Price</p>
                <p className="text-5xl font-bold text-blue-600">
                  ${aiPricing.suggested_price}
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Range: ${aiPricing.min_price} - ${aiPricing.max_price}
                </p>
              </div>

              {/* Confidence */}
              <div className="mt-4">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-600">Confidence</span>
                  <span className="font-medium">{Math.round(aiPricing.confidence * 100)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full transition-all"
                    style={{ width: `${aiPricing.confidence * 100}%` }}
                  />
                </div>
              </div>

              {/* Reasoning */}
              <div className="mt-4 p-3 bg-white rounded-lg">
                <p className="text-sm text-gray-600">
                  <Info className="w-4 h-4 inline mr-1 text-blue-500" />
                  {aiPricing.reasoning}
                </p>
              </div>

              {/* Factors */}
              {aiPricing.factors && aiPricing.factors.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-sm font-medium text-gray-700">Price Factors:</p>
                  {aiPricing.factors.map((f, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">{f.factor}</span>
                      <span className="text-gray-900 font-medium">{f.impact}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Request Summary */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <h4 className="font-medium text-gray-900">Request Summary</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-500">Category:</span>
                  <p className="font-medium">{selectedCategory?.icon} {selectedCategory?.label}</p>
                </div>
                <div>
                  <span className="text-gray-500">Urgency:</span>
                  <p className={`font-medium text-${selectedUrgency?.color}-600`}>
                    {selectedUrgency?.label}
                  </p>
                </div>
              </div>
              <div>
                <span className="text-gray-500 text-sm">Description:</span>
                <p className="text-sm text-gray-900 mt-1">{formData.description}</p>
              </div>
            </div>

            {/* Notice */}
            <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-800">Pending Provider Approval</p>
                <p className="text-amber-700 mt-1">
                  Your request will be sent to nearby providers. The final price may vary based on 
                  the provider's assessment of the actual work needed.
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1 h-12"
              >
                Go Back
              </Button>
              <Button
                onClick={() => setStep(3)}
                className="flex-1 h-12 bg-blue-600 hover:bg-blue-700"
                data-testid="proceed-to-confirm-btn"
              >
                Accept Price
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </>
        )}

        {/* Step 3: Final Confirmation */}
        {step === 3 && (
          <>
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Ready to Submit</h3>
              <p className="text-gray-500 mt-2">
                Your special service request will be sent to nearby providers for approval.
              </p>
            </div>

            {/* Final Summary */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-4">
              <div className="flex justify-between items-center pb-3 border-b">
                <span className="text-gray-600">Service Category</span>
                <span className="font-medium">{selectedCategory?.icon} {selectedCategory?.label}</span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b">
                <span className="text-gray-600">Urgency</span>
                <Badge className={`bg-${selectedUrgency?.color}-100 text-${selectedUrgency?.color}-700`}>
                  {selectedUrgency?.label}
                </Badge>
              </div>
              <div className="flex justify-between items-center pb-3 border-b">
                <span className="text-gray-600">Estimated Price</span>
                <span className="text-xl font-bold text-blue-600">${aiPricing?.suggested_price}</span>
              </div>
              {formData.vehicle_make && (
                <div className="flex justify-between items-center pb-3 border-b">
                  <span className="text-gray-600">Vehicle</span>
                  <span className="font-medium">
                    {formData.vehicle_year} {formData.vehicle_make} {formData.vehicle_model}
                  </span>
                </div>
              )}
              
              {/* Pricing Info */}
              <div className="pt-2 text-xs text-gray-500 space-y-1">
                <p className="flex items-center gap-1">
                  <Info className="w-3 h-3" />
                  Labor charges (78%) are not taxed
                </p>
                <p className="flex items-center gap-1">
                  <Info className="w-3 h-3" />
                  Parts include 16.8% markup + 7% tax
                </p>
                <p className="flex items-center gap-1">
                  <Info className="w-3 h-3" />
                  Tips & donations available after service completion
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setStep(2)}
                className="flex-1 h-12"
              >
                Go Back
              </Button>
              <Button
                onClick={submitRequest}
                disabled={loading}
                className="flex-1 h-12 bg-green-600 hover:bg-green-700"
                data-testid="submit-special-request-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Submit Request
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
